/* eslint-disable no-alert */
/* eslint-disable react/no-danger */
import React, { useState, useRef, useEffect } from 'react'

import {
  Card,
  TagComponent,
  ChevronDown,
  ChevronUp,
  Button,
  Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import {
  string, arrayOf, shape, bool, number,
} from 'prop-types'

import PreviewEditButton from 'Pods/ItemPage/Components/PreviewEditButton/PreviewEditButton'
import { usePreviewMode } from 'Pods/ItemPage/context/PreviewModeContext'
import AreaHeader from 'Pods/ItemPage/TopSection/Components/Shared/AreaHeader/AreaHeader'
import useSentry from 'Pods/Shared/Hooks/useSentry'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import ItemPageBreadcrumbs from './Components/ItemPageBreadcrumbs/ItemPageBreadcrumbs'

function DescriptionArea({
  breadcrumbs,
  description,
  descriptionAi = null,
  aiDescription = false,
  relatedTags: initialTags = EMPTY_ARRAY,
  canDeleteTags = false,
  showHeaderOnly = false,
  extractingCoreStyles = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showReadMore, setShowReadMore] = useState(false)
  const [tags, setTags] = useState(initialTags)
  const contentRef = useRef(null)
  const { captureException } = useSentry()
  const { isPreview } = usePreviewMode()
  const displayedDescription = aiDescription && descriptionAi ? descriptionAi : description

  const handleTagDelete = async (tagId) => {
    const confirmation = window.confirm('Are you sure you want to delete this tag?')
    if (!confirmation) return

    try {
      await axios.delete(`/api/internal/tags/${tagId}`)
      setTags((prevTags) => prevTags.filter((tag) => tag.id !== tagId))
    } catch (error) {
      captureException(error, {
        tags: {
          component: 'DescriptionArea',
          action: 'deleteTag',
        },
        extra: {
          tagId,
        },
      })
    }
  }

  useEffect(() => {
    const calculateMaxHeight = () => {
      const width = window.innerWidth
      if (width < 640) {
        return 68
      }

      if (width < 960) {
        return 180
      }

      if (width < 1280) {
        return 168
      }

      return 180
    }

    const checkContentHeight = () => {
      if (contentRef.current) {
        const maxHeight = calculateMaxHeight()
        const contentHeight = contentRef.current.scrollHeight
        setShowReadMore(contentHeight > maxHeight)
      }
    }

    checkContentHeight()
    window.addEventListener('resize', checkContentHeight)
    return () => window.removeEventListener('resize', checkContentHeight)
  }, [displayedDescription])

  if (showHeaderOnly) {
    return (
      <Card hasBorder isRounded customClass="description-area">
        <div className="description-area__header">
          <ItemPageBreadcrumbs breadcrumbs={breadcrumbs} />
        </div>
      </Card>
    )
  }

  return (
    <Card hasBorder isRounded customClass="description-area">
      <div className="description-area__header">
        <ItemPageBreadcrumbs breadcrumbs={breadcrumbs} />
      </div>
      <div className="description-area__description-header">
        <AreaHeader
          title="DESCRIPTION"
          tooltipId="description-area-tooltip"
          className="description-area__mobile-header"
          titleTag="span"
        />
      </div>
      <div className="description-area__body">
        <div className="description-area__body-content-wrapper">
          <div
            ref={contentRef}
            className={`description-area__body-content ${
              !isExpanded ? 'description-area__body-content--collapsed' : ''
            }`}
            dangerouslySetInnerHTML={{ __html: displayedDescription }}
          />
          <Button
            customClass={(showReadMore || extractingCoreStyles)
              ? "description-area__read-more-button"
              : "description-area__read-more-button--hidden"
            }
            onClick={() => setIsExpanded(!isExpanded)}
            trailIcon={<Icon svgComponent={isExpanded ? ChevronUp : ChevronDown} />}
            type="secondary"
            variant="with-icon"
            size="small"
            aria-label={isExpanded ? 'Read less' : 'Read more'}
            aria-expanded={isExpanded}
            style={{ maxWidth: '132px', padding: '0 0 0 8px', whiteSpace: 'nowrap' }}
          >
            {isExpanded ? 'Read less' : 'Read more'}
          </Button>
        </div>
        {isPreview && <PreviewEditButton className="preview-edit-button--description" />}
      </div>
      {tags.length > 0 && (
        <div className="description-area__related-tags">
          <span className="description-area__related-tags-title">Related Tags</span>
          {tags.map((tag) => (
            <TagComponent
              key={tag.id}
              type="ghost"
              href={tag.active ? tag.url : undefined}
              onDeleteClick={canDeleteTags ? () => handleTagDelete(tag.id) : undefined}
              data-tag-id={tag.id}
              className={tag.active ? 'description-area__related-tag-link' : undefined}
            >
              {tag.text}
            </TagComponent>
          ))}
        </div>
      )}
    </Card>
  )
}

DescriptionArea.propTypes = {
  breadcrumbs: arrayOf(
    shape({
      title: string.isRequired,
      url: string,
      isActive: bool,
    }),
  ).isRequired,
  description: string.isRequired,
  descriptionAi: string,
  aiDescription: bool,
  relatedTags: arrayOf(
    shape({
      id: number.isRequired,
      text: string.isRequired,
      active: bool,
      url: string,
    }),
  ),
  canDeleteTags: bool,
  showHeaderOnly: bool,
  extractingCoreStyles: bool,
}

export default DescriptionArea
